import {
    CheckCircle,
    Email,
    Person,
    Phone,
    Save
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useMicrospecDynamicUIContext } from "../../../../../GlobalProvider";
import { getOrDefault } from '../../../../../Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../../../../Themes';

const AccountDetailsForm = () => {
    const theme = useTheme();
    const { cloudProvider, config } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { user, userProfile } = cloudProvider;

    // Get additional data configuration
    const application = getOrDefault(config.application, {});
    const auth = getOrDefault(application.auth, {});
    const userProfileConfig = getOrDefault(auth.userProfile, {});
    const attributes = userProfile.attributes;

    // Enhanced state management
    const [formData, setFormData] = useState({
        firstName: attributes.firstName || '',
        lastName: attributes.lastName || ''
    });

    const systemAttributes = [
        {
            key: "email",
            displayName: {
                type: "Internal",
                text: "emailAddress"
            },
            type: "email",
            defaultValue: userProfile.attributes.email,
            multiline: false,
            required: !!user.email,
            disableUpdate: !!user.email,
            dir: "ltr",
            regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
            icon: <Email />
        },
        {
            key: "phone",
            displayName: {
                type: "Internal",
                text: "phone"
            },
            type: "tel",
            defaultValue: userProfile.attributes.phone,
            multiline: false,
            required: !!user.phoneNumber,
            disableUpdate: !!user.phoneNumber,
            dir: "ltr",
            icon: <Phone />,
            addDividerAfter: true
        },
    ];

    const additionalData = [...systemAttributes, ...getOrDefault(userProfileConfig.additionalData, [])];


    const [additionalFieldValues, setAdditionalFieldValues] = useState(() => {
        const initialValues = {};
        additionalData.forEach(field => {
            if (field) {
                initialValues[field.key] = attributes[field.key] || field.defaultValue || (field.type === 'number' ? 0 : '');
            }
        });
        return initialValues;
    });

    // Form state management
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    // Track initial values for change detection
    const [initialValues] = useState({
        firstName: attributes.firstName || '',
        lastName: attributes.lastName || '',
        ...(() => {
            const initial = {};
            additionalData.forEach(field => {
                if (field) {
                    initial[field.key] = attributes[field.key] || field.defaultValue || (field.type === 'number' ? 0 : '');
                }
            });
            return initial;
        })()
    });

    // Validation functions
    const validateField = useCallback((key, value, field) => {
        if (field?.required && (!value || value.toString().trim() === '')) {
            return localizationProvider.translateByType({
                type: "Internal",
                text: "fieldRequired"
            }) || "This field is required";
        }

        if (field?.regex && value) {
            const regex = new RegExp(field.regex);
            if (!regex.test(value)) {
                if (field.key === 'email') {
                    return localizationProvider.translateByType({
                        type: "Internal",
                        text: "invalidEmail"
                    }) || "Please enter a valid email address";
                }
                return "Invalid format";
            }
        }

        if (field?.type === 'number') {
            const numValue = Number(value);
            if (field.minValue !== undefined && numValue < field.minValue) {
                return `Minimum value is ${field.minValue}`;
            }
            if (field.maxValue !== undefined && numValue > field.maxValue) {
                return `Maximum value is ${field.maxValue}`;
            }
        }

        return '';
    }, [localizationProvider]);

    // Check for changes
    useEffect(() => {
        const currentValues = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            ...additionalFieldValues
        };

        const hasFormChanges = Object.keys(currentValues).some(key =>
            currentValues[key] !== initialValues[key]
        );

        setHasChanges(hasFormChanges);
    }, [formData, additionalFieldValues, initialValues]);

    // Handle form field changes
    const handleFieldChange = (key, value) => {
        if (key === 'firstName' || key === 'lastName') {
            setFormData(prev => ({ ...prev, [key]: value }));
        } else {
            setAdditionalFieldValues(prev => ({ ...prev, [key]: value }));
        }

        // Mark field as touched
        setTouched(prev => ({ ...prev, [key]: true }));

        // Validate field
        const field = additionalData.find(f => f?.key === key) ||
            (key === 'firstName' || key === 'lastName' ? { required: true } : null);

        if (field) {
            const error = validateField(key, value, field);
            setErrors(prev => ({ ...prev, [key]: error }));
        }
    };

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validate all fields
        const newErrors = {};

        // Validate basic fields
        ['firstName', 'lastName'].forEach(key => {
            const value = formData[key];
            const error = validateField(key, value, { required: true });
            if (error) newErrors[key] = error;
        });

        // Validate additional fields
        additionalData.forEach(field => {
            if (field) {
                const value = additionalFieldValues[field.key];
                const error = validateField(field.key, value, field);
                if (error) newErrors[field.key] = error;
            }
        });

        setErrors(newErrors);

        // If there are errors, don't submit
        if (Object.keys(newErrors).some(key => newErrors[key])) {
            setErrorMessage(localizationProvider.translateByType({
                type: "Internal",
                text: "pleaseFixErrors"
            }) || "Please fix the errors before submitting");
            setShowError(true);
            return;
        }

        setLoading(true);
        setShowError(false);

        try {
            const updateData = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                additionalData: additionalFieldValues
            };

            await userProfile.methods.updateUserProfile(updateData);

            setShowSuccess(true);
            setHasChanges(false);
        } catch (error) {
            console.error('Error updating profile:', error);
            setErrorMessage(error.message || 'Failed to update profile');
            setShowError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Grid item lg={8} md={6} xs={12} sx={{ marginBottom: 2 }}>
                <Card
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        boxShadow: theme.shadows[4],
                        borderRadius: 3,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: theme.shadows[8]
                        }
                    }}
                >
                    <CardHeader
                        avatar={
                            <Avatar
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    boxShadow: `0 4px 20px ${theme.palette.primary.main}30`
                                }}
                            >
                                <Person />
                            </Avatar>
                        }
                        title={
                            <Typography variant="h6" sx={{ fontWeight: 600, mx: 1 }}>
                                {localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "account"
                                })}
                            </Typography>
                        }
                        subheader={
                            <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                                {localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "updateYourAccountInfo"
                                })}
                            </Typography>
                        }
                        sx={{ pb: 1 }}
                    />
                    <Divider />

                    <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            {/* Personal Information Section */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 2,
                                        color: theme.palette.primary.main,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <Person fontSize="small" />
                                    {localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "personalInformation"
                                    }) || "Personal Information"}
                                </Typography>
                            </Grid>

                            <Grid item md={6} xs={12}>
                                <TextField
                                    fullWidth
                                    label={localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "firstName"
                                    })}
                                    id="firstName"
                                    name="firstName"
                                    required
                                    value={formData.firstName}
                                    variant="outlined"
                                    error={touched.firstName && !!errors.firstName}
                                    helperText={touched.firstName && errors.firstName}
                                    onChange={(event) => handleFieldChange('firstName', event.target.value)}
                                    onBlur={() => setTouched(prev => ({ ...prev, firstName: true }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: `0 4px 20px ${theme.palette.primary.main}15`
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: `0 4px 20px ${theme.palette.primary.main}25`
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item md={6} xs={12}>
                                <TextField
                                    fullWidth
                                    label={localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "lastName"
                                    })}
                                    id="lastName"
                                    name="lastName"
                                    required
                                    value={formData.lastName}
                                    variant="outlined"
                                    error={touched.lastName && !!errors.lastName}
                                    helperText={touched.lastName && errors.lastName}
                                    onChange={(event) => handleFieldChange('lastName', event.target.value)}
                                    onBlur={() => setTouched(prev => ({ ...prev, lastName: true }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: `0 4px 20px ${theme.palette.primary.main}15`
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: `0 4px 20px ${theme.palette.primary.main}25`
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            {/* Contact Information Section */}
                            {additionalData.some(field => field && (field.key === 'email' || field.key === 'phone')) && (
                                <>
                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 2,
                                                color: theme.palette.primary.main,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <Email fontSize="small" />
                                            {localizationProvider.translateByType({
                                                type: "Internal",
                                                text: "contactInformation"
                                            })}
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {/* Additional Data Fields */}
                            {additionalData.map((field, index) => {
                                if (!field) return null;

                                return (
                                    <Fragment key={field.key}>
                                        <Grid item md={6} xs={12}>
                                            <TextField
                                                fullWidth
                                                label={localizationProvider.translateByType(field.displayName)}
                                                id={field.key}
                                                name={field.key}
                                                type={field.type}
                                                multiline={field.type === 'text' && field.multiline}
                                                rows={field.type === 'text' && field.multiline ? 3 : 1}
                                                value={additionalFieldValues[field.key] || ''}
                                                required={field.required}
                                                variant="outlined"
                                                error={touched[field.key] && !!errors[field.key]}
                                                helperText={touched[field.key] && errors[field.key]}
                                                disabled={field.disableUpdate}
                                                dir={field.dir}
                                                onChange={(event) => {
                                                    if (field.disableUpdate) return;
                                                    const value = field.type === 'number'
                                                        ? Number(event.target.value)
                                                        : event.target.value;
                                                    handleFieldChange(field.key, value);
                                                }}
                                                onBlur={() => setTouched(prev => ({ ...prev, [field.key]: true }))}
                                                InputProps={{
                                                    startAdornment: field.icon && (
                                                        <InputAdornment position="start">
                                                            {field.icon}
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: field.disableUpdate && (
                                                        <InputAdornment position="end">
                                                            <Tooltip title={
                                                                localizationProvider.translateByType({
                                                                    type: "Internal",
                                                                    text: "verifiedField"
                                                                }) || "This field is verified and cannot be changed"
                                                            }>
                                                                <CheckCircle color="success" fontSize="small" />
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        transition: 'all 0.3s ease',
                                                        ...(!field.disableUpdate && {
                                                            '&:hover': {
                                                                boxShadow: `0 4px 20px ${theme.palette.primary.main}15`
                                                            },
                                                            '&.Mui-focused': {
                                                                boxShadow: `0 4px 20px ${theme.palette.primary.main}25`
                                                            }
                                                        })
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        {field.addDividerAfter && index !== additionalData.length - 1 && (
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 2 }} />
                                            </Grid>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </Grid>
                    </CardContent>

                    <Divider />

                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading || !hasChanges}
                                startIcon={loading ? <CircularProgress size={20} sx={{ mx: 1 }} /> : <Save sx={{ mx: 1 }} />}
                                sx={{
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    boxShadow: `0 4px 20px ${theme.palette.primary.main}30`,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 30px ${theme.palette.primary.main}40`,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                                    },
                                    '&:disabled': {
                                        background: theme.palette.action.disabledBackground,
                                        transform: 'none',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {loading ? localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "saving"
                                }) : (
                                    localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "save"
                                    })
                                )}
                            </Button>
                            {/* Success Message */}
                            {showSuccess && (
                                <Alert
                                    severity="success"
                                    // variant="filled"
                                    onClose={() => setShowSuccess(false)}
                                    sx={{
                                        mb: 2,
                                        borderRadius: 2,
                                        boxShadow: `0 4px 20px ${theme.palette.success.main}30`,
                                        animation: 'fadeIn 0.3s ease-in-out',
                                        '& .MuiAlert-action': {
                                            alignItems: 'flex-start',
                                            paddingTop: '4px'
                                        }
                                    }}
                                >
                                    {localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "profileUpdatedSuccessfully"
                                    })}
                                </Alert>
                            )}

                            {/* Error Message */}
                            {showError && (
                                <Alert
                                    severity="error"
                                    variant="filled"
                                    onClose={() => setShowError(false)}
                                    sx={{
                                        mb: 2,
                                        borderRadius: 2,
                                        boxShadow: `0 4px 20px ${theme.palette.error.main}30`,
                                        animation: 'fadeIn 0.3s ease-in-out',
                                        '& .MuiAlert-action': {
                                            alignItems: 'flex-start',
                                            paddingTop: '4px'
                                        }
                                    }}
                                >
                                    {errorMessage}
                                </Alert>
                            )}
                            {
                                !showError && !showSuccess && (<Box />)
                            }
                        </Box>
                    </Box>
                </Card>
            </Grid>
        </>
    );
};

export default AccountDetailsForm;
